import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, FileArchive, FileSpreadsheet, Loader2, Map as MapIcon, Play, Trash2, UploadCloud, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Modal from '@/components/common/Modal';
import { Button, IconButton } from '@/components/common/Button';
import UploadList from '@/components/upload/UploadList';
import UploadPreviewPanel from '@/components/upload/UploadPreviewPanel';
import UploadIssues from '@/components/upload/UploadIssues';
import { useToast } from '@/components/common/Toast';
import { useUploadFile } from '@/hooks/useUploadFile';
import { useStartImport } from '@/hooks/useStartImport';
import { useUploads } from '@/hooks/useUploads';
import { uploadService } from '@/services/upload.service';
import { UPLOAD_ACCEPT, UPLOAD_STATUS_REFRESH_MS, uploadsKey } from '@/config/upload';
import { formatBytes } from '@/utils/format';
import { codeMessage, isImportInProgress, isLoneShapefilePart, uploadErrorMessage } from '@/utils/upload';
import { localeTag } from '@/i18n/format';
import { cn } from '@/utils/cn';
import { Upload, UploadStartRequest, WorkspaceRights } from '@/types/upload';

interface UploadWizardProps {
  workspace: string;
  workspaceName: string;
  /** The caller's rights in the workspace: the file and row limits. */
  rights?: WorkspaceRights;
  onClose: () => void;
  /** An import is in the database: the workspace lists its layer and, when asked, shows it on the map. */
  onImported?: (upload: Upload, showOnMap: boolean) => void;
}

type Step = 'choose' | 'check' | 'importing' | 'done' | 'failed';
const STEPS: Step[] = ['choose', 'check', 'importing', 'done'];

/**
 * Bringing a file into a workspace, in one dialog over the whole app that stays open through every step: choose the
 * file, check its columns and how its places are given, the import into the database, and its result. Once the
 * import has ended the upload is cleaned away (the server deleted the file already; the upload's record goes too) and
 * only the layer is left. Closing the dialog while the import runs asks first: the import goes on without it.
 */
const UploadWizard: React.FC<UploadWizardProps> = ({ workspace, workspaceName, rights, onClose, onImported }) => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<Step>('choose');
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [current, setCurrent] = useState<Upload | null>(null);
  const [request, setRequest] = useState<UploadStartRequest | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const send = useUploadFile(workspace);
  const start = useStartImport(workspace);
  const { data: uploads = [] } = useUploads(workspace);
  const unfinished = uploads.filter((u) => u.id !== current?.id);

  const tooLarge = !!file && !!rights?.maxBytes && file.size > rights.maxBytes;
  const lonePart = !!file && isLoneShapefilePart(file.name);

  // The import, followed until it ends.
  const following = useQuery({
    queryKey: ['upload', workspace, current?.id],
    queryFn: () => uploadService.get(workspace, current!.id),
    enabled: step === 'importing' && !!current,
    refetchInterval: (q) => (q.state.data && !isImportInProgress(q.state.data.status) ? false : UPLOAD_STATUS_REFRESH_MS),
    retry: 2,
  });
  useEffect(() => {
    const u = following.data;
    if (step !== 'importing' || !u || isImportInProgress(u.status)) return;
    setCurrent(u);
    setStep(u.status === 'DONE' ? 'done' : 'failed');
    queryClient.invalidateQueries({ queryKey: uploadsKey(workspace) });
    if (u.status === 'DONE') onImported?.(u, false);
  }, [following.data, step]); // eslint-disable-line react-hooks/exhaustive-deps

  /** An ended import leaves no upload behind: its record goes once its result has been seen. */
  const cleanUp = useMutation({
    mutationFn: (u: Upload) => uploadService.remove(workspace, u.id),
    onSettled: () => queryClient.invalidateQueries({ queryKey: uploadsKey(workspace) }),
  });
  const discard = useMutation({
    mutationFn: (u: Upload) => uploadService.remove(workspace, u.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: uploadsKey(workspace) }),
    onError: (e) => addToast(uploadErrorMessage(e), 'error'),
  });

  const leaveResult = () => {
    if ((step === 'done' || step === 'failed') && current?.canManage) cleanUp.mutate(current);
  };

  const chooseFile = (chosen: File | null) => setFile(chosen);
  const clearFile = () => {
    setFile(null);
    // Otherwise choosing the same file again does not fire onChange.
    if (fileInput.current) fileInput.current.value = '';
  };

  const read = () => {
    if (!file || tooLarge || lonePart) return;
    send.mutate(file, {
      onSuccess: (upload) => {
        clearFile();
        setCurrent(upload);
        setRequest(null);
        setStep(upload.status === 'WAITING' ? 'check' : isImportInProgress(upload.status) ? 'importing' : 'failed');
      },
    });
  };

  const begin = () => {
    if (!current || !request) return;
    start.mutate({ id: current.id, request }, {
      onSuccess: (u) => {
        setCurrent(u);
        setStep('importing');
      },
    });
  };

  const again = () => {
    leaveResult();
    setCurrent(null);
    setRequest(null);
    setStep('choose');
  };

  const close = useCallback(() => {
    if (step === 'importing' && !window.confirm(t('uploadFlow.close_while_importing'))) return;
    leaveResult();
    onClose();
  }, [step, current]); // eslint-disable-line react-hooks/exhaustive-deps

  const onChecked = useCallback((r: UploadStartRequest | null, p: string | null) => {
    setRequest(r);
    setProblem(p);
  }, []);

  const shown = following.data ?? current;
  const FileIcon = file && /\.zip$/i.test(file.name) ? FileArchive : FileSpreadsheet;

  // ------------------------------------------------------------------------------------------------ footer

  let footer: React.ReactNode = null;
  let footerStart: React.ReactNode = null;
  if (step === 'choose') {
    footer = (
      <>
        <Button variant="secondary" onClick={close}>{t('uploadFlow.cancel')}</Button>
        <Button variant="primary" icon={UploadCloud} loading={send.isPending} disabled={!file || tooLarge || lonePart}
                onClick={read} data-testid="upload-read">
          {send.isPending ? t('uploads.reading') : t('uploadFlow.read')}
        </Button>
      </>
    );
  } else if (step === 'check' && current) {
    footerStart = problem ? <span className="text-gn-warning">{t(`uploads.${problem}`)}</span> : null;
    footer = (
      <>
        <Button variant="ghost" icon={Trash2} disabled={start.isPending || discard.isPending}
                onClick={() => discard.mutate(current, { onSuccess: () => { setCurrent(null); setStep('choose'); } })}>
          {t('uploadFlow.discard')}
        </Button>
        <Button variant="primary" icon={Play} loading={start.isPending} disabled={!request} onClick={begin} data-testid="upload-start">
          {start.isPending ? t('uploads.starting') : t('uploadFlow.start')}
        </Button>
      </>
    );
  } else if (step === 'importing') {
    footerStart = t('uploadFlow.importing_keep_open');
  } else if (step === 'done' && shown) {
    footer = (
      <>
        <Button variant="secondary" onClick={again}>{t('uploadFlow.another')}</Button>
        {shown.layerKey && !shown.dataOnly ? (
          <Button variant="primary" icon={MapIcon} data-testid="upload-show"
                  onClick={() => { onImported?.(shown, true); leaveResult(); onClose(); }}>
            {t('uploadFlow.show_on_map')}
          </Button>
        ) : (
          <Button variant="primary" onClick={close}>{t('uploadFlow.close')}</Button>
        )}
      </>
    );
  } else if (step === 'failed') {
    footer = (
      <>
        <Button variant="secondary" onClick={close}>{t('uploadFlow.close')}</Button>
        <Button variant="primary" icon={UploadCloud} onClick={again}>{t('uploadFlow.try_again')}</Button>
      </>
    );
  }

  return (
    <Modal title={t('uploads.title')} subtitle={workspaceName} size="xl" tall={step === 'check'} onClose={close}
           footer={footer} footerStart={footerStart} testId="upload-dialog">
      <Stepper step={step} />

      {step === 'choose' && (
        <div className="space-y-6">
          <label
            data-testid="upload-drop"
            className={cn('flex flex-col items-center justify-center gap-3 text-center rounded-xl border-2 border-dashed px-6 py-10 cursor-pointer transition-colors',
              dragging ? 'border-gn-blue bg-gn-blue-light' : 'border-gn hover:border-gn-blue hover:bg-gn-hover')}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => { e.preventDefault(); setDragging(false); chooseFile(e.dataTransfer.files?.[0] ?? null); }}>
            <UploadCloud size={36} className="text-gn-blue" />
            <span className="text-base font-medium text-gn-primary">{dragging ? t('uploadFlow.drop_active') : t('uploadFlow.drop_title')}</span>
            <span className="text-sm text-gn-muted max-w-xl">{t('uploads.hint')}</span>
            {rights && (
              <span className="text-xs text-gn-muted">
                {t('uploads.limits', { size: formatBytes(rights.maxBytes), rows: rights.maxRows.toLocaleString(localeTag()) })}
              </span>
            )}
            <input ref={fileInput} type="file" accept={UPLOAD_ACCEPT} className="sr-only" data-testid="upload-input"
                   onChange={(e) => chooseFile(e.target.files?.[0] ?? null)} />
          </label>

          {file && (
            <div className="flex items-center gap-3 rounded-lg border border-gn-light bg-gn-surface px-4 py-3">
              <FileIcon size={20} className="shrink-0 text-gn-secondary" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate" title={file.name}>{file.name}</p>
                <p className="text-xs text-gn-muted">{formatBytes(file.size)}</p>
              </div>
              <IconButton icon={X} size="sm" label={t('buttons.clear')} onClick={clearFile} disabled={send.isPending} />
            </div>
          )}
          {lonePart && <Notice tone="danger">{t('uploadShp.code_shapefile_needs_zip')}</Notice>}
          {tooLarge && rights && <Notice tone="danger">{t('uploads.too_large_local', { size: formatBytes(rights.maxBytes) })}</Notice>}

          {unfinished.length > 0 && (
            <section className="space-y-2">
              <h3 className="text-sm font-semibold">{t('uploadFlow.unfinished')}</h3>
              <UploadList workspace={workspace} onCheck={(u) => { setCurrent(u); setRequest(null); setStep('check'); }} />
            </section>
          )}
        </div>
      )}

      {step === 'check' && current && (
        <div className="space-y-2">
          <h3 className="text-base font-semibold break-words">{current.fileName}</h3>
          <UploadPreviewPanel key={current.id} upload={current} disabled={start.isPending} onChange={onChecked} />
        </div>
      )}

      {step === 'importing' && (
        <div className="flex flex-col items-center justify-center text-center gap-4 py-12" data-testid="upload-importing">
          <Loader2 size={40} className="animate-spin text-gn-blue" />
          <div className="space-y-1">
            <p className="text-lg font-semibold">
              {shown?.status === 'QUEUED' ? t('uploadFlow.queued') : t('uploadFlow.importing_title')}
            </p>
            <p className="text-sm text-gn-muted">{shown?.layerName || shown?.fileName}</p>
            {!!shown?.rowsRead && (
              <p className="text-sm text-gn-secondary tabular-nums">
                {t('uploadFlow.importing_rows', { rows: shown.rowsRead.toLocaleString(localeTag()) })}
              </p>
            )}
          </div>
        </div>
      )}

      {step === 'done' && shown && (
        <div className="space-y-5" data-testid="upload-done">
          <div className="flex items-start gap-4 rounded-xl border border-gn-success bg-gn-success-light px-5 py-4">
            <CheckCircle2 size={28} className="shrink-0 text-gn-success" />
            <div className="space-y-1">
              <p className="text-base font-semibold">{t('uploadFlow.done_title')}</p>
              <p className="text-sm">
                {shown.dataOnly
                  ? t('uploadFlow.done_data', { count: shown.rowsCreated })
                  : t('uploadFlow.done_layer', { name: shown.layerName || shown.fileName, count: shown.rowsCreated })}
              </p>
              <p className="text-xs text-gn-muted">{t('uploadFlow.done_cleanup')}</p>
            </div>
          </div>
          {shown.rowsWithoutGeometry > 0 && <Notice tone="warning">{t('uploads.without_place', { count: shown.rowsWithoutGeometry })}</Notice>}
          {shown.issueCount > 0 && (
            <section className="space-y-2">
              <h3 className="text-sm font-semibold">{t('uploadFlow.issues')}</h3>
              <UploadIssues upload={shown} />
            </section>
          )}
        </div>
      )}

      {step === 'failed' && shown && (
        <div className="space-y-5" data-testid="upload-failed">
          <div className="flex items-start gap-4 rounded-xl border border-gn-danger bg-gn-danger-light px-5 py-4">
            <AlertTriangle size={28} className="shrink-0 text-gn-danger" />
            <div className="space-y-1">
              <p className="text-base font-semibold">{t('uploadFlow.failed_title')}</p>
              <p className="text-sm">{codeMessage(shown.errorCode) ?? shown.error}</p>
            </div>
          </div>
          {shown.issueCount > 0 && <UploadIssues upload={shown} />}
        </div>
      )}
    </Modal>
  );
};

/** Where the dialog is: file, check, import, done. */
const Stepper: React.FC<{ step: Step }> = ({ step }) => {
  const { t } = useTranslation();
  const at = STEPS.indexOf(step === 'failed' ? 'done' : step);
  return (
    <ol className="flex items-center gap-2 mb-6 text-xs sm:text-sm" aria-label={t('uploadFlow.steps')}>
      {STEPS.map((s, i) => (
        <li key={s} className="flex items-center gap-2 min-w-0">
          <span className={cn('flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
            i < at || (i === at && step === 'done') ? 'bg-gn-success text-gn-inverse' : i === at ? (step === 'failed' ? 'bg-gn-danger text-gn-inverse' : 'bg-gn-blue text-gn-inverse') : 'bg-gn-tertiary text-gn-muted')}>
            {i + 1}
          </span>
          <span className={cn('truncate', i === at ? 'font-semibold text-gn-primary' : 'text-gn-muted')}>{t(`uploadFlow.step_${s}`)}</span>
          {i < STEPS.length - 1 && <span className="hidden sm:block w-8 h-px bg-gn-tertiary" style={{ backgroundColor: 'var(--gn-border)' }} />}
        </li>
      ))}
    </ol>
  );
};

const Notice: React.FC<{ tone: 'danger' | 'warning'; children: React.ReactNode }> = ({ tone, children }) => (
  <p className={cn('flex items-start gap-2 text-sm', tone === 'danger' ? 'text-gn-danger' : 'text-gn-warning')}>
    <AlertTriangle size={16} className="mt-0.5 shrink-0" />
    <span>{children}</span>
  </p>
);

export default UploadWizard;
